import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Radio, Alert, message } from "antd";
import {
  VideoCameraOutlined,
  PictureOutlined,
  FileOutlined,
  YoutubeOutlined,
  LinkOutlined,
} from "@ant-design/icons";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";
import TextArea from "antd/es/input/TextArea";
import MediaSelector from "../Selectors/MediaSelector";
import { getMediaIcon, formatDuration } from "../../utils/Misc";

const schema = yup.object().shape({
  title: yup.string().required("Playlist title is required"),
  orientation: yup.string().required("Orientation is required"),
  media: yup
    .array()
    .min(1, "At least one media is required")
    .required("Media is required"),
  status: yup.string().required("Status is required"),
});

const PlaylistForm = ({
  isModalOpen,
  editingPlaylist,
  closeModal,
  handleUpdateList,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDataSet, setIsDataSet] = useState(false);
  const [mediaOptions, setMediaOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingPlaylist },
    defaultValues: {
      status: "active",
      orientation: "landscape",
    },
  });

  const selectedOrientation = watch("orientation");
  const orientationFilteredMediaOptions = mediaOptions.filter(
    (media) => !media.orientation || media.orientation === selectedOrientation
  );

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/playlists/masters`
      );

      const status = response.data.data.status;
      const media = response.data.data.media;

      setStatusOptions(status);
      setMediaOptions(
        media.map((mediaItem) => {
          const fileData = mediaItem.file_data
            ? JSON.parse(mediaItem.file_data)
            : {};
          const fileName = fileData.file_name || "";
          let extension = fileName.split(".").pop() || ""; // get extension

          if (extension === "") {
            extension = mediaItem.media_type;
          }

          return {
            value: mediaItem.id,
            type: mediaItem.media_type,
            duration_seconds: mediaItem.duration_seconds,
            orientation: mediaItem.orientation,
            label: (
              <>
                {getMediaIcon(mediaItem.media_type)}
                {mediaItem.title}{" "}
                <span style={{ color: "#888" }}>
                  ({extension.toUpperCase()})
                </span>
              </>
            ),
          };
        })
      );
      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  const fetchPlaylist = async (playlistId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/playlists/${playlistId}`
      );
      const mediaData =
        response.data.data.media && JSON.parse(response.data.data.media);
      const editFormData = {
        ...response.data.data,
        media: mediaData,
      };
      setSelectedMedia(
        mediaData
          .filter((pl) => {
            const matched = mediaOptions.find(
              (media) => media.value === pl.media_id
            );
            if (matched) {
              return true;
            } else {
              return false;
            }
          })
          .map((pl) => {
            const matched = mediaOptions.find(
              (media) => media.value === pl.media_id
            );
            if (matched) {
              return {
                ...matched,
                duration: pl.duration,
              };
            } else {
              return false;
            }
          })
      );
      reset(editFormData);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const handleMedia = (selectedMedia) => {
    setValue(
      "media",
      selectedMedia.map((p) => ({
        media_id: p.value,
        duration: p.duration,
      })),
      { shouldDirty: true }
    );
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
  }, []);

  useEffect(() => {
    if (isDataSet && editingPlaylist) {
      fetchPlaylist(editingPlaylist.id);
    }
  }, [isDataSet, editingPlaylist]);

  const onSubmitPlaylist = async (data) => {
    let differFormFields = {};
    if (editingPlaylist !== null) {
      differFormFields = {
        id: editingPlaylist.id,
      };
    }
    const finalFormData = {
      title: data.title.trim(),
      description: (data.description && data.description.trim()) || "",
      media: data.media,
      orientation: data.orientation,
      status: data.status,
      ...differFormFields,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingPlaylist === null
        ? `${process.env.REACT_APP_BASE_URL}/api/playlists/create`
        : `${process.env.REACT_APP_BASE_URL}/api/playlists/update`;

    try {
      if (editingPlaylist === null) {
        await axios.post(url, newFormData);
        // Reset form after successful submission
        // reset(newFormData);
        message.success("Playlist created successfully");
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          // reset(data);
          message.success("Playlist updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        // const errorField = error.response.data.field;
        console.log(errorData);
        // setShowErrors(false);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);

        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  return (
    <Modal
      title={editingPlaylist ? "Edit Playlist" : "Add New Playlist"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        // setEditingPlaylist(null);
        closeModal();
      }}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <>
              <div className="col-sm-12 mb-3">
                <Alert message={errorsMessage} type="error" closeIcon={false} />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit(onSubmitPlaylist)}>
            <div className="mb-3">
              <label className="form-label">
                Playlist Title <span className="text-danger">*</span>
              </label>
              <Controller
                name="title"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.title && (
                <div className="text-danger small">{errors.title.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => <TextArea {...field} />}
              />
              {errors.description && (
                <div className="text-danger small">
                  {errors.description.message}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label me-3">
                Orientation <span className="text-danger">*</span>
              </label>
              <Controller
                name="orientation"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      // Previously selected media may not match the new
                      // orientation, so clear the selection when it changes.
                      setSelectedMedia([]);
                      setValue("media", [], { shouldDirty: true });
                    }}
                  >
                    <Radio value="landscape">Landscape</Radio>
                    <Radio value="portrait">Portrait</Radio>
                  </Radio.Group>
                )}
              />
              {errors.orientation && (
                <div className="text-danger small">{errors.orientation.message}</div>
              )}
            </div>
            <div className="mb-3">
              <Controller
                name="media"
                control={control}
                render={() => (
                  <MediaSelector
                    mediaOptions={orientationFilteredMediaOptions}
                    selectedMedia={selectedMedia}
                    setSelectedMedia={setSelectedMedia}
                    handleMedia={handleMedia}
                    showErrors={!!errors.media}
                  />
                )}
              />
              {selectedMedia.length > 0 && (
                <Alert
                  type="info"
                  showIcon
                  className="mt-2"
                  message={`Total Duration (preview): ${formatDuration(
                    selectedMedia.reduce(
                      (total, item) =>
                        total +
                        ((item.type === "video" || item.type === "youtube") &&
                        item.duration_seconds != null
                          ? item.duration_seconds
                          : Number(item.duration) || 0),
                      0
                    )
                  )}`}
                />
              )}
            </div>
            <div className="mb-3">
              <label className="form-label me-3">Status</label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    {statusOptions.map((status) => (
                      <Radio value={status.value} key={status.value}>
                        {status.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              />
              {errors.status && (
                <div className="text-danger small">{errors.status.message}</div>
              )}
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting}
            >
              {!submitting ? (
                editingPlaylist ? (
                  "Update Playlist"
                ) : (
                  "Add Playlist"
                )
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default PlaylistForm;
